

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { DeclineReasonCode, OrderStatus, SellerOrderDetail } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateAdminOrders } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type UpdateStoreOrderStatusInput = {
  storeId: string;
  orderId: string;
  status: OrderStatus;
  statusEtaDays: number | null;
  declineReasonCode?: DeclineReasonCode;
  declineReasonNote?: string;
};

export function useUpdateStoreOrderStatusMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ storeId, orderId, ...body }: UpdateStoreOrderStatusInput) =>
      apiJsonClient<SellerOrderDetail>(endpoints.admin.storeOrderStatus(storeId, orderId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: async (_data, variables) => {
      await invalidateAdminOrders(queryClient, variables.storeId);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.storeOrderById(variables.storeId, variables.orderId),
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.storeOrderProof(variables.storeId, variables.orderId),
          exact: true,
        }),
      ]);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
  };
}
